import React, { useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../../assets/contexts/AuthContext';
import PopupWrapper from '../../../../components/popup/PopupWrapper';
import { getButtonClass } from '../../../../components/popup/popupUtils';
import { isGlowAdmin } from '../support/supportPermissions';
import CompanyOrgSignupPreview from '../report/forms/CompanyOrgSignupPreview';
import PersonalSignupPreview from '../report/forms/PersonalSignupPreview';
import PocTechRequirementsPreview from '../report/forms/PocTechRequirementsPreview';
import PocProgramSupportPreview from '../report/forms/PocProgramSupportPreview';
import PocApplicationLayoutDocumentPreview from '../report/forms/PocApplicationLayoutDocumentPreview';
import MouPreview from '../report/forms/MouPreview';
import { downloadFormPreviewPdf, resolveFormPreviewPdfFileBaseName } from '../report/utils/formPreviewPdfDownload';
import '../report/styles/documentFormBuilder.css';

/**
 * 신청서 미리보기 팝업 (공통)
 * formType === 'personal_signup' 시 PersonalSignupPreview(개인 회원가입 신청서)
 * formType === 'company_org_signup' 시 CompanyOrgSignupPreview(기관·기업 회원가입 신청서) 사용
 * formType === 'poc_tech_requirements' 시 PocTechRequirementsPreview(PoC 연구 수행용 수요기술 요구사항) 사용
 * formType === 'poc_program_support' 시 PocProgramSupportPreview(PoC 프로그램 수행 지원서) 사용
 * documentFormPreview: 개인 회원가입 — individual_user_info VIEW + flat → buildFormDataFromLegacy 후
 * PocTechRequirementsPreview(문서표) + showSignupDocumentFooter(회원가입 전용 하단 푸터)
 */
const PreviewPopup = ({ config, onClose }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canDownloadPdf = isGlowAdmin(user);
  const pdfCaptureRef = useRef(null);
  const [pdfBusy, setPdfBusy] = useState(false);

  const layout = config?.layout || [];
  const formValues = config?.formValues || {};
  const signatureImage = config?.signatureImage || null;
  const documentTitle = config?.documentTitle ?? t('popup.previewPopup.documentTitle');
  const introText = config?.introText ?? t('popup.previewPopup.defaultIntroText');
  const isDocumentFormSignupPreview =
    config?.documentFormPreview &&
    Array.isArray(config?.structureJson?.layout) &&
    config.structureJson.layout.length > 0 &&
    config?.dataJson;
  const isPersonalSignup =
    !isDocumentFormSignupPreview &&
    config?.formType === 'personal_signup' &&
    config?.structureJson &&
    config?.dataJson;
  const isCompanyOrgSignup = config?.formType === 'company_org_signup' && config?.structureJson && config?.dataJson;
  /** layoutRows 기반 문서 미리보기 — formType이 poc_program_support여도 이 variant가 우선 */
  const isPocApplicationLayoutPreview =
    config?.previewVariant === 'poc_application_layout' &&
    config?.structureJson &&
    config?.dataJson;
  const isPocTechRequirements = config?.formType === 'poc_tech_requirements' && config?.structureJson && config?.dataJson;
  const isPocProgramSupport = config?.formType === 'poc_program_support' && config?.structureJson && config?.dataJson;
  const isMou = config?.formType === 'mou' && config?.data;

  const handlePdfDownload = useCallback(async () => {
    if (!isGlowAdmin(user)) {
      window.alert(t('popup.previewPopup.alertAdminOnlyPdf'));
      return;
    }
    const root = pdfCaptureRef.current;
    if (!root) {
      window.alert(t('popup.previewPopup.alertNoContent'));
      return;
    }
    setPdfBusy(true);
    try {
      await downloadFormPreviewPdf(root, resolveFormPreviewPdfFileBaseName(config));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      window.alert(e?.message || t('popup.previewPopup.alertPdfError'));
    } finally {
      setPdfBusy(false);
    }
  }, [config, user, t]);

  const renderCellValue = (field, value) => {
    if (field.type === 'IMAGE' && value) {
      return (
        <img src={value} alt={field.label} className="form-preview-doc-img" />
      );
    }
    if (field.type === 'BOOLEAN') {
      return value ? t('popup.previewPopup.agreed') : '-';
    }
    return (value ?? '') || '-';
  };

  return (
    <PopupWrapper
      config={config}
      onClose={onClose}
      displayType={config?.displayType || 'modal'}
      className="popup-form-preview"
    >
      <div className="main-popup-body form-preview-doc-body">
        <div ref={pdfCaptureRef} className="form-preview-pdf-capture-root">
        {isDocumentFormSignupPreview ? (
          <div className="form-preview-doc-inner form-preview-document-form">
            <PocTechRequirementsPreview
              signatureDataUrl={signatureImage || undefined}
              structureJson={config.structureJson}
              dataJson={config.dataJson}
              localImageSrcByKey={config.localImageSrcByKey || {}}
              showSignupDocumentFooter
            />
          </div>
        ) : isPersonalSignup ? (
          <div className="form-preview-doc-inner form-preview-personal">
            <PersonalSignupPreview
              signatureDataUrl={signatureImage || undefined}
              structureJson={config.structureJson}
              dataJson={config.dataJson}
            />
          </div>
        ) : isCompanyOrgSignup ? (
          <div className="form-preview-doc-inner form-preview-company-org">
            <CompanyOrgSignupPreview
              signatureDataUrl={signatureImage || undefined}
              structureJson={config.structureJson}
              dataJson={config.dataJson}
            />
          </div>
        ) : isPocApplicationLayoutPreview ? (
          <div className="form-preview-doc-inner form-preview-support">
            <PocApplicationLayoutDocumentPreview
              signatureDataUrl={signatureImage || undefined}
              structureJson={config.structureJson}
              dataJson={config.dataJson}
            />
          </div>
        ) : isPocTechRequirements ? (
          <div className="form-preview-doc-inner form-preview-poc">
            <PocTechRequirementsPreview
              signatureDataUrl={signatureImage || undefined}
              structureJson={config.structureJson}
              dataJson={config.dataJson}
              localImageSrcByKey={config.localImageSrcByKey || {}}
            />
          </div>
        ) : isPocProgramSupport ? (
          <div className="form-preview-doc-inner form-preview-support">
            <PocProgramSupportPreview
              signatureDataUrl={signatureImage || undefined}
              structureJson={config.structureJson}
              dataJson={config.dataJson}
            />
          </div>
        ) : isMou ? (
          <div className="form-preview-doc-inner form-preview-mou">
            <MouPreview data={config.data} />
          </div>
        ) : (
        <div className="form-preview-doc-inner">
          <h2 className="form-preview-doc-title">{documentTitle}</h2>
          <p className="form-preview-doc-intro">{introText}</p>

          <div className="form-preview-doc-table-wrap">
            <table className="form-preview-doc-table">
              <tbody>
                {layout.map((block, blockIndex) => {
                  if (block.type === 'SECTION') {
                    const sectionTitle = block.title || (blockIndex === 0 ? t('popup.previewPopup.basicInfo') : '');
                    return (
                      <React.Fragment key={`section-${blockIndex}`}>
                        {sectionTitle && (
                          <tr className="form-preview-doc-section-row">
                            <td colSpan={2} className="form-preview-doc-section-cell">
                              {sectionTitle}
                            </td>
                          </tr>
                        )}
                        {(block.fields || []).map((field) => (
                          <tr key={field.key} className="form-preview-doc-row">
                            <td className="form-preview-doc-label">{field.label}</td>
                            <td className="form-preview-doc-value">
                              {renderCellValue(field, formValues[field.key])}
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  }
                  if (block.type === 'DIVIDER') {
                    return (block.items || []).map((item) => (
                      <tr key={item.key} className="form-preview-doc-row">
                        <td className="form-preview-doc-label">{item.label}</td>
                        <td className="form-preview-doc-value">
                          {renderCellValue(item, formValues[item.key])}
                        </td>
                      </tr>
                    ));
                  }
                  return null;
                })}
                <tr className="form-preview-doc-section-row">
                  <td colSpan={2} className="form-preview-doc-section-cell">
                    {t('popup.previewPopup.signatureSection')}
                  </td>
                </tr>
                <tr className="form-preview-doc-row">
                  <td className="form-preview-doc-label">{t('popup.previewPopup.applicationSignature')}</td>
                  <td className="form-preview-doc-value">
                    {signatureImage ? (
                      <img src={signatureImage} alt={t('popup.previewPopup.signatureAlt')} className="form-preview-doc-signature-img" />
                    ) : (
                      '-'
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        )}
        </div>
      </div>
      {canDownloadPdf ? (
        <div className="main-popup-footer d-flex justify-content-end">
          <button
            type="button"
            className={`btn main-popup-right-btn ${getButtonClass(config?.footerPdfButtonColor || 'btn-primary')}`}
            disabled={pdfBusy}
            onClick={handlePdfDownload}
          >
            {pdfBusy ? t('popup.previewPopup.pdfGenerating') : (config?.footerPdfButtonText || t('popup.previewPopup.downloadPdf'))}
          </button>
        </div>
      ) : null}
      <style>{`
        .form-preview-doc-body {
          overflow-y: auto;
          max-height: 65vh;
          background: #f4f5f6;
        }
        .form-preview-doc-inner {
          padding: 8px 0 16px;
        }
        .form-preview-doc-inner.form-preview-company-org,
        .form-preview-doc-inner.form-preview-personal,
        .form-preview-doc-inner.form-preview-poc,
        .form-preview-doc-inner.form-preview-support,
        .form-preview-doc-inner.form-preview-mou,
        .form-preview-doc-inner.form-preview-document-form {
          padding: 0;
        }
        .form-preview-doc-title {
          font-size: 1.25rem;
          font-weight: 600;
          text-align: center;
          color: #1f2937;
          margin: 0 0 12px;
        }
        .form-preview-doc-intro {
          font-size: 14px;
          line-height: 1.6;
          color: #374151;
          text-align: center;
          margin: 0 0 20px;
          padding: 0 8px;
        }
        .form-preview-doc-table-wrap {
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06);
        }
        .form-preview-doc-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 14px;
          background: #fff;
        }
        .form-preview-doc-row {
          border-bottom: 1px solid #e5e7eb;
        }
        .form-preview-doc-row:last-child {
          border-bottom: none;
        }
        .form-preview-doc-label {
          width: 160px;
          min-width: 140px;
          padding: 10px 12px;
          font-weight: 600;
          color: #374151;
          background: #f9fafb;
          border-right: 1px solid #e5e7eb;
          vertical-align: top;
        }
        .form-preview-doc-value {
          padding: 10px 12px;
          color: #1f2937;
          word-break: break-word;
          vertical-align: top;
        }
        .form-preview-doc-section-row {
          border-bottom: 1px solid #e5e7eb;
        }
        .form-preview-doc-section-cell {
          padding: 10px 12px;
          font-weight: 600;
          color: #374151;
          background: #f3f4f6;
          border-bottom: 1px solid #e5e7eb;
        }
        .form-preview-doc-img {
          max-width: 180px;
          max-height: 100px;
          object-fit: contain;
          border: 1px solid #e5e7eb;
          border-radius: 4px;
        }
        .form-preview-doc-signature-img {
          max-width: 220px;
          height: auto;
          display: block;
          border: 1px solid #e5e7eb;
          border-radius: 4px;
        }
        @media (max-width: 576px) {
          .form-preview-doc-label {
            width: 120px;
            min-width: 100px;
          }
        }
      `}</style>
    </PopupWrapper>
  );
};

export default PreviewPopup;
